import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useOrganization } from '../../../utils/OrganizationContext';
import { useAuth } from '../../../utils/Auth';
import API from '../../../API';
import RoleSelector from './RoleSelector';
import { canManageUsers, canRemoveUser, getRoleDisplayName } from '../../../utils/Auth/roleUtils';
import { transition, variants, sortMenuVariants, sortButtonsVariants } from '../variants';
import styles from './Members.module.css';

const Members = () => {
  const navigate = useNavigate();
  const { activeOrgId, currentUser, config } = useAuth();
  const {
    organizations,
    orgMembers,
    orgInvites,
    currentUserOrgData,
    loadingMembers,
    loadingInvites
  } = useOrganization();

  const [error, setError] = useState('');
  const [confirmingMemberId, setConfirmingMemberId] = useState(null);
  const [confirmingInviteId, setConfirmingInviteId] = useState(null);
  const [sortBy, setSortBy] = useState('default'); // 'default', 'name', 'email', 'role'
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const currentOrg = organizations?.find(org => org.id === activeOrgId);

  // Sort members based on selected criteria
  const getSortedMembers = () => {
    if (!orgMembers || orgMembers.length === 0) return [];

    const membersCopy = [...orgMembers];

    const compareAlphabetically = (a, b) => {
      const aLast = a.name_last || a.name_first || a.email || '';
      const bLast = b.name_last || b.name_first || b.email || '';
      const aFirst = a.name_first || '';
      const bFirst = b.name_first || '';
      
      const lastCompare = aLast.toLowerCase().localeCompare(bLast.toLowerCase());
      if (lastCompare !== 0) return lastCompare;
      return aFirst.toLowerCase().localeCompare(bFirst.toLowerCase());
    };

    const compareByEmail = (a, b) => {
      const aEmail = a.email || '';
      const bEmail = b.email || '';
      return aEmail.toLowerCase().localeCompare(bEmail.toLowerCase());
    };

    const compareByRole = (a, b) => {
      const roleOrder = { 'owner': 1, 'admin': 2, 'manager': 3, 'member': 4 };
      const aRole = a.role || 'member';
      const bRole = b.role || 'member';
      
      const roleCompare = (roleOrder[aRole] || 999) - (roleOrder[bRole] || 999);
      if (roleCompare !== 0) return roleCompare;
      
      // Within same role, sort alphabetically
      return compareAlphabetically(a, b);
    };

    if (sortBy === 'default') {
      // Current user first, then alphabetically
      membersCopy.sort((a, b) => {
        if (a.uid === currentUser?.uid) return -1;
        if (b.uid === currentUser?.uid) return 1;
        return compareAlphabetically(a, b);
      });
    } else if (sortBy === 'name') {
      membersCopy.sort(compareAlphabetically);
    } else if (sortBy === 'email') {
      membersCopy.sort(compareByEmail);
    } else if (sortBy === 'role') {
      membersCopy.sort(compareByRole);
    }

    return membersCopy;
  };

  const sortedMembers = getSortedMembers();

  const handleRemoveMember = async (member) => {
    if (!canRemoveUser(currentUserOrgData, member)) {
      setError('You do not have permission to remove this user.');
      return;
    }

    if (member.uid === currentUser.uid) {
      setError('You cannot remove yourself from the organization.');
      return;
    }

    try {
      await API.removeUserFromOrg({
        org_id: activeOrgId,
        user_id: member.uid
      });
      
      // Members list will update automatically via listener
      setError('');
      setConfirmingMemberId(null);
    } catch (err) {
      console.error('Error removing member:', err);
      alert(err?.response?.data?.message || 'Failed to remove member');
      setConfirmingMemberId(null);
    }
  };

  const handleShowConfirm = (memberId) => {
    setConfirmingMemberId(memberId);
  };

  const handleCancelRemove = () => {
    setConfirmingMemberId(null);
  };

  const handleShowInviteConfirm = (inviteId) => {
    setConfirmingInviteId(inviteId);
  };

  const handleCancelInvite = () => {
    setConfirmingInviteId(null);
  };

  const handleInvalidateInvite = async (invite) => {
    try {
      await API.invalidateOrgInvite({
        org_id: activeOrgId,
        invite_token: invite.id
      });

      setError('');
      setConfirmingInviteId(null);
    } catch (err) {
      console.error('Error invalidating invite:', err);
      alert(err?.response?.data?.message || 'Failed to invalidate invite');
      setConfirmingInviteId(null);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await API.updateUserRole({
        org_id: activeOrgId,
        user_id: member.uid,
        role: newRole
      });

      // Members list will update automatically via listener
      setError('');
    } catch (err) {
      console.error('Error updating role:', err);
      alert(err?.response?.data?.message || 'Failed to update role');
      throw err; // Re-throw to let RoleSelector handle the error state
    }
  };

  // Check if current user can manage users
  const userCanManage = currentUserOrgData && canManageUsers(currentUserOrgData, config);

  // Check if organization is at or over team capacity
  const currentMemberCount = orgMembers?.length || 0;
  const teamSizeLimit = currentOrg?.plan?.team_size || 0;
  const isAtCapacity = currentMemberCount >= teamSizeLimit;

  const isLoading = loadingMembers || loadingInvites;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <button className={styles.backButton} onClick={() => navigate('/organizations')}>
              <i className='fas fa-chevron-left'></i>
            </button>
            <h1 className={styles.title}>Manage Team Members</h1>
          </div>
          {currentOrg?.name && (
            <div className={styles.headerBottom}>
              <p className={styles.orgName}>Organization: <strong>{currentOrg.name}</strong></p>
            </div>
          )}
        </div>

        {userCanManage && (
          <div className={styles.actions}>
            <button 
              className={`${styles.inviteButton} ${isAtCapacity ? styles.inviteButtonDisabled : ''}`}
              onClick={() => !isAtCapacity && navigate('/organizations/members/invite')}
              disabled={isAtCapacity}
              title={isAtCapacity ? "Team capacity reached. Upgrade your plan to invite more members." : ""}
            >
              <i className='fas fa-envelope'></i> 
              {isAtCapacity ? 'Team Capacity Reached' : 'Invite New Member'}
            </button>
            {isAtCapacity && (
              <p className={styles.capacityWarning}>
                You've reached your team limit ({currentMemberCount}/{teamSizeLimit}). 
                Upgrade your plan to invite more members.
              </p>
            )}
          </div>
        )}

        {isLoading ? (
          <div className={styles.loading}>Loading team members...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : orgMembers.length === 0 && orgInvites.length === 0 ? (
          <div className={styles.empty}>
            <p>No team members yet. Start by inviting your first team member!</p>
          </div>
        ) : (
          <>
            <motion.div 
              className={styles.sortMenuContainer}
              onClick={() => setSortMenuOpen(!sortMenuOpen)}
              initial="closed"
              animate={sortMenuOpen ? 'open' : 'closed'}
              variants={sortMenuVariants}
            >
              <span className={styles.sortByText}>Sort By</span>
              <motion.div
                className={styles.sortButtonsContainer}
                initial="closed"
                animate={sortMenuOpen ? 'open' : 'closed'}
                variants={sortButtonsVariants}
              >
                <button
                  className={`${styles.sortButton} ${sortBy === 'default' ? styles.sortButtonActive : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortBy('default');
                  }}
                >
                  Default
                </button>
                <button
                  className={`${styles.sortButton} ${sortBy === 'name' ? styles.sortButtonActive : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortBy('name');
                  }}
                >
                  Name
                </button>
                <button
                  className={`${styles.sortButton} ${sortBy === 'email' ? styles.sortButtonActive : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortBy('email');
                  }}
                >
                  Email
                </button>
                <button
                  className={`${styles.sortButton} ${sortBy === 'role' ? styles.sortButtonActive : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortBy('role');
                  }}
                >
                  Role
                </button>
              </motion.div>
            </motion.div>
            <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  {userCanManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map(member => {
                  const displayName = member.name_first && member.name_last 
                    ? `${member.name_first} ${member.name_last}` 
                    : 'N/A';
                  const canRemove = userCanManage && canRemoveUser(currentUserOrgData, member);

                  return (
                    <tr key={member.uid}>
                      <td>
                        {displayName}
                        {member.uid === currentUser?.uid && (
                          <span className={styles.youBadge}>(You)</span>
                        )}
                      </td>
                      <td>{member.email || 'N/A'}</td>
                      <td>
                        {userCanManage ? (
                          <RoleSelector
                            currentUserOrgData={currentUserOrgData}
                            targetUserOrgData={member}
                            onRoleChange={(newRole) => handleRoleChange(member, newRole)}
                          />
                        ) : (
                          <span className={styles.roleDisplay}>
                            {getRoleDisplayName(member.role)}
                          </span>
                        )}
                      </td>
                      {userCanManage && (
                        <td>
                          {canRemove ? (
                            <div className={styles.actionContainer}>
                              <motion.button
                                key='remove/cancel'
                                className={`${styles.removeButton} ${confirmingMemberId === member.uid ? styles.cancelMode : ''}`}
                                onClick={() => confirmingMemberId === member.uid ? handleCancelRemove() : handleShowConfirm(member.uid)}
                                initial={false}
                                animate={confirmingMemberId !== member.uid ? 'initial1' : 'animate1'}
                                variants={variants}
                                transition={transition}
                                disabled={false}
                              >
                                {confirmingMemberId !== member.uid ? (
                                  <><i className='fas fa-times'></i> Remove</>
                                ) : (
                                  <><i className='fas fa-times'></i> Cancel</>
                                )}
                              </motion.button>
                              <motion.button
                                key='deleteConfirm'
                                className={styles.confirmButton}
                                onClick={() => handleRemoveMember(member)}
                                initial={false}
                                animate={confirmingMemberId === member.uid ? 'animate2' : 'initial2'}
                                variants={variants}
                                transition={transition}
                                disabled={false}
                              >
                                <i className='fas fa-check'></i> Confirm
                              </motion.button>
                            </div>
                          ) : (
                            <span className={styles.noAction}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {userCanManage && orgInvites.map(invite => (
                  <tr key={invite.id}>
                    <td>
                      <span className={styles.inviteLabel}>Invite Sent</span>
                    </td>
                    <td>{invite.email || 'N/A'}</td>
                    <td>
                      <span className={styles.pendingRole}>Pending</span>
                    </td>
                    <td>
                      <div className={styles.actionContainer}>
                        <motion.button
                          key='invite/cancel'
                          className={`${styles.removeButton} ${confirmingInviteId === invite.id ? styles.cancelMode : ''}`}
                          onClick={() => confirmingInviteId === invite.id ? handleCancelInvite() : handleShowInviteConfirm(invite.id)}
                          initial={false}
                          animate={confirmingInviteId !== invite.id ? 'initial1' : 'animate1'}
                          variants={variants}
                          transition={transition}
                          disabled={false}
                        >
                          {confirmingInviteId !== invite.id ? (
                            <><i className='fas fa-times'></i> Invalidate</>
                          ) : (
                            <><i className='fas fa-times'></i> Cancel</>
                          )}
                        </motion.button>
                        <motion.button
                          key='inviteConfirm'
                          className={styles.confirmButton}
                          onClick={() => handleInvalidateInvite(invite)}
                          initial={false}
                          animate={confirmingInviteId === invite.id ? 'animate2' : 'initial2'}
                          variants={variants}
                          transition={transition}
                          disabled={false}
                        >
                          <i className='fas fa-check'></i> Confirm
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Members;
